import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import SectionMessage from '@atlaskit/section-message/message';
import SectionMessageAction from '@atlaskit/section-message/message-action';
import Spinner from '@atlaskit/spinner/spinner';

import { RestrictedPage } from '@confluence/no-permission/entry-points/RestrictedPage.ts';
import { PerformanceInsightsPage } from '@confluence/performance-insights/entry-points/PerformanceInsightsPage.ts';
import { PerformanceInsightsAccess } from '@confluence/performance-insights/entry-points/PerformanceInsightsAccess.ts';

const messages = defineMessages({
	loading: {
		id: 'confluence-frontend-server.performance-insights.access.loading',
		defaultMessage: 'Checking access to Performance Monitoring',
		description: 'Accessible label while checking access to Performance Monitoring',
	},
	unavailable: {
		id: 'confluence-frontend-server.performance-insights.access.unavailable',
		defaultMessage: 'Performance Monitoring is unavailable',
		description: 'Title when Performance Monitoring access cannot be verified',
	},
	retry: {
		id: 'confluence-frontend-server.performance-insights.access.retry',
		defaultMessage: 'Retry',
		description: 'Retry checking access to Performance Monitoring',
	},
	description: {
		id: 'confluence-frontend-server.performance-insights.access.description',
		defaultMessage: 'We couldn’t check your access. Try again.',
		description: 'Shown when the entitlement query fails or returns incomplete information',
	},
});

const AccessLoading = (): React.JSX.Element => {
	const intl = useIntl();
	return (
		<div role="status">
			<Spinner label={intl.formatMessage(messages.loading)} />
		</div>
	);
};

const AccessUnavailable = ({ onRetry }: { onRetry: () => void }): React.JSX.Element => {
	const intl = useIntl();
	return (
		<div role="alert">
			<SectionMessage
				appearance="warning"
				title={intl.formatMessage(messages.unavailable)}
				actions={[
					<SectionMessageAction key="retry" onClick={onRetry}>
						<FormattedMessage {...messages.retry} />
					</SectionMessageAction>,
				]}
			>
				<FormattedMessage {...messages.description} />
			</SectionMessage>
		</div>
	);
};

const renderUnavailable = (onRetry: () => void) => <AccessUnavailable onRetry={onRetry} />;

export const PerformanceInsightsEntitledRoute = (): React.JSX.Element => (
	<PerformanceInsightsAccess
		denied={<RestrictedPage showSuperAdminGateway={false} />}
		loading={<AccessLoading />}
		renderUnavailable={renderUnavailable}
		interactionName="PerformanceInsightsRouteEntitlement"
	>
		<PerformanceInsightsPage />
	</PerformanceInsightsAccess>
);
